import { useEffect, useState } from 'react';
import { Button, Checkbox, Spin } from 'antd';
import styles from './PrivacyNoticeModal.module.scss';
import {
  getPrivacyNotice,
  acknowledgePrivacyNotice,
  type PrivacyNoticeDto,
} from '../../apis/PrivacyNoticeService';

interface PrivacyNoticeModalProps {
  open: boolean;
  onAcknowledge: () => void;
  readOnly?: boolean;
  onClose?: () => void;
}

const PrivacyNoticeModal = ({ open, onAcknowledge, readOnly = false, onClose }: PrivacyNoticeModalProps) => {
  const [notice, setNotice] = useState<PrivacyNoticeDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setChecked(false);

    getPrivacyNotice(controller.signal)
      .then((data) => {
        // If the service resolved with requiresAcknowledgment=false, skip the modal
        if (!data.requiresAcknowledgment && !readOnly) {
          onAcknowledge();
        } else {
          setNotice(data);
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          // 404 = no active notice configured; let app through
          if (err?.response?.status === 404) {
            if (readOnly) {
              setError('No privacy notice has been published yet.');
            } else {
              onAcknowledge();
            }
          } else {
            setError('Failed to load Privacy Notice.');
          }
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [open, onAcknowledge]);

  if (!open) return null;

  const handleAcknowledge = async () => {
    if (!notice || !checked) return;

    setSubmitting(true);
    try {
      await acknowledgePrivacyNotice({
        privacyNoticeId: notice.id,
        accepted: true,
      });
      onAcknowledge();
    } catch (err: any) {
      // 409 — notice version changed, re-fetch
      if (err?.response?.status === 409) {
        setError(null);
        setChecked(false);
        setLoading(true);
        try {
          const refreshed = await getPrivacyNotice();
          setNotice(refreshed);
        } catch {
          setError('Failed to reload Privacy Notice.');
        } finally {
          setLoading(false);
        }
      } else {
        setError('Failed to record acknowledgment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} data-testid="privacy-notice-overlay">
      <div className={styles.dialog} data-testid="privacy-notice-dialog">
        <div className={styles.titleBar} data-testid="privacy-notice-title">
          <span className={styles.title}>{notice?.title || 'Privacy Notice'}</span>
          {readOnly && onClose && (
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close"
              data-testid="privacy-notice-close-btn"
            >
              &times;
            </button>
          )}
        </div>
        <div className={styles.body} data-testid="privacy-notice-body">
          {loading && <Spin data-testid="privacy-notice-loading" />}
          {error && <p className={styles.error} data-testid="privacy-notice-error">{error}</p>}
          {!loading && !error && notice && (
            <p className="whitespace-pre-wrap">{notice.content}</p>
          )}
        </div>
        {!readOnly && !loading && !error && notice && (
          <div className={styles.footer}>
            <div className={styles.checkboxRow}>
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                data-testid="privacy-notice-checkbox"
              >
                I have read and acknowledge the Privacy Notice
              </Checkbox>
            </div>
            <Button
              type="primary"
              onClick={handleAcknowledge}
              data-testid="privacy-notice-acknowledge-btn"
              className={styles.acknowledgeButton}
              disabled={!checked || submitting}
              loading={submitting}
            >
              Acknowledge
            </Button>
          </div>
        )}
        {readOnly && !loading && !error && (
          <div className={styles.footer}>
            <Button
              onClick={onClose}
              data-testid="privacy-notice-close-footer-btn"
            >
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivacyNoticeModal;
